/**
 * Хранилище авторизации (Zustand).
 * Управляет состоянием аутентификации пользователя,
 * токенами доступа и информацией о текущем пользователе.
 * Поддерживает режим имперсонации (вход от имени другого пользователя).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';

interface AuthState {
  /** Текущий пользователь */
  user: User | null;
  /** JWT-токен доступа */
  accessToken: string | null;
  /** JWT-токен обновления */
  refreshToken: string | null;
  /** Флаг авторизации */
  isAuthenticated: boolean;

  /** Флаг режима имперсонации */
  isImpersonating: boolean;
  /** Оригинальный пользователь (администратор) при имперсонации */
  originalUser: User | null;
  /** Оригинальный access-токен администратора */
  originalAccessToken: string | null;
  /** Оригинальный refresh-токен администратора */
  originalRefreshToken: string | null;
}

interface AuthActions {
  /** Установить данные после успешного входа */
  login: (user: User, accessToken: string, refreshToken: string) => void;
  /** Выход из системы — очистка всех данных */
  logout: () => void;
  /** Обновить токены (после refresh) */
  setTokens: (accessToken: string, refreshToken: string) => void;
  /** Обновить данные пользователя */
  setUser: (user: User) => void;

  /** Начать имперсонацию: сохранить текущего пользователя и переключиться */
  startImpersonation: (user: User, accessToken: string, refreshToken: string) => void;
  /** Завершить имперсонацию: восстановить исходного администратора */
  stopImpersonation: () => void;
}

type AuthStore = AuthState & AuthActions;

/** Начальное состояние */
const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isImpersonating: false,
  originalUser: null,
  originalAccessToken: null,
  originalRefreshToken: null,
};

/**
 * Хранилище авторизации.
 * Данные сохраняются в localStorage для поддержки сессии между перезагрузками.
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      login: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        }),

      logout: () => set(initialState),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      setUser: (user) => set({ user }),

      startImpersonation: (user, accessToken, refreshToken) => {
        const state = get();
        // Сохраняем оригинал ТОЛЬКО при первом входе в имперсонацию.
        // Если уже имперсонируем (цепочка: admin → user1 → user2),
        // original остаётся от первого перехода — всегда возвращаемся к исходному админу.
        const keepOriginal = state.isImpersonating;
        set({
          originalUser: keepOriginal ? state.originalUser : state.user,
          originalAccessToken: keepOriginal ? state.originalAccessToken : state.accessToken,
          originalRefreshToken: keepOriginal ? state.originalRefreshToken : state.refreshToken,
          user,
          accessToken,
          refreshToken,
          isImpersonating: true,
        });
      },

      stopImpersonation: () => {
        const state = get();
        // Восстанавливаем исходного администратора
        set({
          user: state.originalUser,
          accessToken: state.originalAccessToken,
          refreshToken: state.originalRefreshToken,
          isImpersonating: false,
          originalUser: null,
          originalAccessToken: null,
          originalRefreshToken: null,
        });
      },
    }),
    {
      name: 'asubp-auth', // ключ в localStorage
      partialize: (state) => ({
        // Сохраняем токены, данные пользователя и состояние имперсонации
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        isImpersonating: state.isImpersonating,
        originalUser: state.originalUser,
        originalAccessToken: state.originalAccessToken,
        originalRefreshToken: state.originalRefreshToken,
      }),
    },
  ),
);
